// ============================================
// Categories Hooks - TanStack Query
// ============================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type { Category, CategoryListParams, CreateCategoryRequest, UpdateCategoryRequest, PaginationParams } from '@conai/shared-types';
import { useCategoriesService } from '../providers/ApiProvider';
import { queryKeys } from '../config/query-client';
import { useI18n } from '../i18n';

interface CategoryFilters extends PaginationParams {
  limit?: number;
  parentId?: string | null;
  isActive?: boolean;
  search?: string;
  [key: string]: unknown;
}

function mapCategoryFilters(filters: CategoryFilters): CategoryListParams {
  return {
    page: filters.page,
    limit: filters.limit ?? filters.page_size,
    ...(filters.search ? { search: filters.search } : {}),
    ...(filters.parentId !== undefined ? { parent: filters.parentId ?? 'null' } : {}),
    ...(filters.isActive !== undefined ? { is_active: filters.isActive } : {}),
  };
}

/**
 * Hook to list categories with pagination and filters
 */
export function useCategories(filters: CategoryFilters = {}) {
  const categoriesService = useCategoriesService();
  const mappedFilters = mapCategoryFilters(filters);

  return useQuery({
    queryKey: queryKeys.categories.list(filters),
    queryFn: () => categoriesService.list(mappedFilters),
  });
}

/**
 * Hook to get aggregate category stats (total / active / root / leaf).
 * Computed server-side; independent of search/status filters. Auto-refreshes
 * on create/update/delete via categories.all invalidation.
 */
export function useCategoryStats() {
  const categoriesService = useCategoriesService();

  return useQuery({
    queryKey: queryKeys.categories.stats(),
    queryFn: () => categoriesService.getStats(),
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to get category tree
 */
export function useCategoryTree() {
  const categoriesService = useCategoriesService();

  return useQuery({
    queryKey: queryKeys.categories.tree(),
    queryFn: async () => {
      const response = await categoriesService.list({ page: 1, limit: 1000 });
      return categoriesService.buildTree(response.results ?? []);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - tree doesn't change often
  });
}

/**
 * Hook to get a single category by ID
 */
export function useCategory(id: string, options?: { enabled?: boolean }) {
  const categoriesService = useCategoriesService();

  return useQuery({
    queryKey: queryKeys.categories.detail(id),
    queryFn: () => categoriesService.getById(id),
    enabled: options?.enabled !== false && !!id,
  });
}

/**
 * Hook to create a new category
 */
export function useCreateCategory() {
  const categoriesService = useCategoriesService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (data: CreateCategoryRequest) => categoriesService.create(data),
    onSuccess: (newCategory) => {
      // Invalidate categories queries
      queryClient.invalidateQueries({ queryKey: queryKeys.categories.all });
      toast.success(t('toastCategoryCreated', { name: newCategory.name }));
    },
  });
}

/**
 * Hook to update a category
 */
export function useUpdateCategory() {
  const categoriesService = useCategoriesService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCategoryRequest }) =>
      categoriesService.update(id, data),
    onSuccess: (updatedCategory) => {
      // Update cache
      queryClient.setQueryData(queryKeys.categories.detail(updatedCategory.id), updatedCategory);
      // Invalidate list only (tree only needed for category picker, not every update)
      queryClient.invalidateQueries({ queryKey: queryKeys.categories.lists() });
      // Stats (active/root/leaf) can change on update (e.g. toggle active, move parent)
      queryClient.invalidateQueries({ queryKey: queryKeys.categories.stats() });
      toast.success(t('toastCategoryUpdated', { name: updatedCategory.name }));
    },
  });
}

/**
 * Sắp xếp / đổi cha hàng loạt (sheet 2 mục #17) — POST /categories/bulk-update/.
 * Một request nguyên tử cho cả danh sách, thay cho cách PATCH từng cái.
 * Bốn mã lỗi 400 do caller xử lý (categories_not_found,
 * parent_categories_not_found, category_cycle, duplicate_category_ids) —
 * hook cố ý KHÔNG toast lỗi để trang tự hiện câu phù hợp với thao tác.
 */
export function useBulkUpdateCategories() {
  const categoriesService = useCategoriesService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (items: Array<{ id: string; sort_order?: number; parent_id?: string | null }>) =>
      categoriesService.bulkUpdate(items),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.categories.lists() });
      // Cây dùng cho picker cha — đổi thứ tự/đổi cha là nó cũng lệch
      queryClient.invalidateQueries({ queryKey: queryKeys.categories.all });
    },
  });
}

/**
 * Hook to delete a category
 */
export function useDeleteCategory() {
  const categoriesService = useCategoriesService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (id: string) => categoriesService.delete(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.categories.detail(deletedId) });
      // Invalidate all category queries (tree structure may have changed)
      queryClient.invalidateQueries({ queryKey: queryKeys.categories.all });
      toast.success(t('toastCategoryDeleted'));
    },
  });
}
